import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/header';
import { useLanguage } from '../../../helpers/language-context';
import { supabase } from '../../../supabaseClient';

const NewsReleaseDetail = () => {
    const { translations, language } = useLanguage();
    const { newsId } = useParams();
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            if (!newsId) return;

            const { data, error } = await supabase
                .from('newsfeed')
                .select('*')
                .eq('id', newsId)
                .single();

            if (error) {
                console.error('Error fetching news item:', error);
            } else {
                setNewsItem(data);
            }
            setLoading(false);
        };

        fetchNews();
    }, [newsId]);

    const getLocalized = (item, field) => {
        if (!item) return '';
        return language === 'ms'
            ? (item[`${field}_ms`] || item[`${field}_en`])
            : (item[`${field}_en`] || item[`${field}_ms`]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString(language === 'ms' ? 'ms-MY' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#28357B]"></div>
            </div>
        );
    }

    if (!newsItem) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
                <h2 className="text-2xl font-bold text-gray-700">News not found</h2>
                <Link to="/media/news-release" className="text-[#28357B] hover:underline">Back to News Release</Link>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <Header
                title={getLocalized(newsItem, 'title')}
                back={true}
                backLink='/media/news-release'
                backText={translations.media.newsRelease || 'News Release'}
            >
                <div className="flex justify-center md:justify-end items-center mt-6 md:mt-[10vh]">
                    <div className="text-white text-sm font-medium">
                        {formatDate(newsItem.date)}
                    </div>
                </div>
            </Header>

            <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                <article className="space-y-8">
                    {/* Cover Image */}
                    {(newsItem.cover_image || newsItem.image) && (
                        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg">
                            <img
                                src={newsItem.cover_image || newsItem.image}
                                alt={getLocalized(newsItem, 'title')}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Subtitle */}
                    {getLocalized(newsItem, 'subtitle') && (
                        <h3 className="text-2xl font-semibold text-[#28357B]">
                            {getLocalized(newsItem, 'subtitle')}
                        </h3>
                    )}

                    {/* Content (Description 1) */}
                    {getLocalized(newsItem, 'description1') && (
                        <div className="prose prose-lg text-gray-700 max-w-none whitespace-pre-wrap">
                            {getLocalized(newsItem, 'description1')}
                        </div>
                    )}

                    {/* Secondary Image - Only show if we have a distinct cover_image, or if we want to show 'image' as secondary when cover_image is present */}
                    {/* If cover_image exists, we assume 'image' is the secondary image. */}
                    {newsItem.cover_image && newsItem.image && (
                        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg mt-8">
                            <img
                                src={newsItem.image}
                                alt="Story detail"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Description 2 */}
                    {getLocalized(newsItem, 'description2') && (
                        <div className="prose prose-lg text-gray-700 max-w-none whitespace-pre-wrap mt-8">
                            {getLocalized(newsItem, 'description2')}
                        </div>
                    )}
                </article>
            </div>
        </div>
    );
}

export default NewsReleaseDetail;
